/**
 * SignInDialog — global sign-in prompt.
 *
 * Mounted once at the root of the app and controlled by state
 * inside AuthContext (`signInOpen` + `openSignIn()`). Every existing
 * `useAuth().login()` caller in the app now opens this dialog instead
 * of jumping straight to Google OAuth — so university claim-owners
 * on Microsoft 365 (Cambridge, LSE, Warwick…) never see a dead-end
 * Google-only button.
 *
 * Two paths:
 *   1. Google OAuth — same behaviour as before, via
 *      `loginWithGoogle()` on the context.
 *   2. Magic-link — POST /api/auth/magic-link/request, generic
 *      "Check your inbox" confirmation regardless of whether the
 *      inbox exists (prevents enumeration).
 */
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiErrorText } from "@/lib/apiError";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";

export default function SignInDialog() {
  const { signInOpen, closeSignIn, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setEmail("");
    setSent(false);
    setSending(false);
  };

  const onOpenChange = (open) => {
    if (!open) {
      reset();
      closeSignIn();
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      toast.error("Enter your email address.");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/magic-link/request", { email: clean });
      setSent(true);
    } catch (err) {
      toast.error(apiErrorText(err, "Couldn't send just now — try again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={signInOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="signin-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">
            Sign in to Which University Advisor
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Pick whichever is easiest — both give you the same
            account.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div
            className="mt-2 p-4 rounded-md border border-emerald-200 bg-emerald-50"
            data-testid="signin-dialog-sent-panel"
          >
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-editorial text-base">
                Check your inbox.
              </span>
            </div>
            <p className="mt-1 text-sm text-emerald-900">
              We've emailed a sign-in link to{" "}
              <span className="font-mono">{email}</span>. The link
              expires in 15 minutes and only works once.
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-3 text-[11px] font-underground-sc tracking-wider text-emerald-800 hover:underline"
              data-testid="signin-dialog-again-btn"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <Button
                onClick={loginWithGoogle}
                data-testid="signin-dialog-google-btn"
                className="w-full"
              >
                Sign in with Google <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-400">
                Fastest if your account is on Google (personal gmail
                or a Google-Workspace institution).
              </p>
            </div>

            <div
              className="flex items-center gap-3 text-[10px] font-underground-sc tracking-widest text-stone-400"
              aria-hidden="true"
            >
              <div className="flex-1 h-px bg-stone-200" />
              <span>or</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>

            <form onSubmit={submit} className="space-y-2" data-testid="signin-dialog-magic-form">
              <label
                htmlFor="signin-magic-email"
                className="text-[11px] font-underground-sc tracking-wider text-stone-600 flex items-center gap-1"
              >
                <Mail className="h-3 w-3" /> Email me a sign-in link
              </label>
              <Input
                id="signin-magic-email"
                type="email"
                autoComplete="email"
                placeholder="you@your-domain.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signin-dialog-email-input"
                maxLength={200}
                required
              />
              <Button
                type="submit"
                variant="outline"
                disabled={sending || !email.trim()}
                data-testid="signin-dialog-magic-submit-btn"
                className="w-full"
              >
                {sending ? "Sending…" : "Email me the link"}
              </Button>
              <p className="text-[11px] font-underground-sc tracking-wider text-stone-400">
                Works with any inbox. University claim-owners: use
                your institutional address (Microsoft 365, Google
                Workspace, your own server) — we'll auto-verify on
                domain match. Public providers (gmail, outlook.com)
                can still sign in but won't auto-claim a university.
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
